/**
 * useStorage Hook
 * 
 * React hook for uploading images to Cloudinary with progress tracking.
 */

import { useState, useCallback } from 'react'
import {
  uploadFile,
  uploadEntityImage,
  deleteFile,
  deleteFileByUrl,
  validateImageFile,
} from '../services/storage'

/**
 * Hook for uploading files to Firebase Storage
 * @returns {object} - { upload, uploadEntityImage, deleteFile, progress, uploading, error }
 */
export function useStorage() {
  const [progress, setProgress] = useState(0)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)

  const upload = useCallback(async (file, path) => {
    // Validate file
    const validation = validateImageFile(file)
    if (!validation.valid) {
      setError(validation.error)
      throw new Error(validation.error)
    }

    setUploading(true)
    setError(null)
    setProgress(0)

    try {
      const result = await uploadFile(file, path, setProgress)
      setUploading(false)
      setProgress(100)
      return result
    } catch (err) {
      setError(err.message || 'Upload failed')
      setUploading(false)
      throw err
    }
  }, [])

  const uploadForEntity = useCallback(async (file, entityType, entityId) => {
    // Validate file
    const validation = validateImageFile(file)
    if (!validation.valid) {
      setError(validation.error)
      throw new Error(validation.error)
    }

    setUploading(true)
    setError(null)
    setProgress(0)

    try {
      const result = await uploadEntityImage(file, entityType, entityId, setProgress)
      setUploading(false)
      setProgress(100)
      return result
    } catch (err) {
      setError(err.message || 'Upload failed')
      setUploading(false)
      throw err
    }
  }, [])

  const removeFile = useCallback(async (pathOrUrl) => {
    setError(null)
    try {
      if (pathOrUrl.startsWith('http')) {
        await deleteFileByUrl(pathOrUrl)
      } else {
        await deleteFile(pathOrUrl)
      }
    } catch (err) {
      setError(err.message || 'Delete failed')
      throw err
    }
  }, [])

  const resetProgress = useCallback(() => {
    setProgress(0)
    setError(null)
  }, [])

  return {
    upload,
    uploadForEntity,
    removeFile,
    resetProgress,
    progress,
    uploading,
    error,
  }
}

export default useStorage

